import { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { financialsApi, type GlCostLine } from '@/lib/apiClient';
import { glLinesFor } from '@/lib/glByGroup';
import { StateView } from '@/components';
import { fmtDate, toDateInputValue, todayInputValue } from '@/lib/dates';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';

/**
 * The GL entries behind one Actual-cost figure.
 *
 * Finance's standing question about job cost has been "what is in that number" —
 * answerable until now only by re-running the query in Intacct (job 10878
 * reconciliation, 2026-07-29). Clicking an Actual cell opens this.
 *
 * TWO THINGS THIS DELIBERATELY SHOWS
 *
 * 1. Each line's OWN Intacct cost type, next to the group the tab filed it
 *    under. Those disagree by design: the tab groups a cost CODE by the type
 *    the estimate budgeted it as, because matching actual to budget at the
 *    (code × type) grain manufactured phantom overruns. So opening "Labor" can
 *    legitimately list an entry typed M. That is the actual shape of the data,
 *    and hiding it is what made the split unauditable.
 *
 * 2. A cost-type breakdown of whatever is on screen. That is precisely the
 *    reconciliation finance is doing right now to map the 20 Intacct cost types
 *    the tab does not yet recognise, all of which currently land in "Other".
 *
 * The line set comes from `group.codes` — the cost codes the tab itself used to
 * build the cell — so the subtotal here always equals the figure clicked.
 */

const money = (v: number) => (v < 0 ? '-' : '') + '$' + Math.abs(Math.round(v)).toLocaleString('en-US');

export interface GlDetailTarget {
  /** Heading — the cost type group or the cost code that was clicked. */
  label: string;
  sub: string | null;
  /** Cost codes forming the clicked cell; `''` is the null-code bucket.
   *  Null = the whole job (the header-level export/browse). */
  codes: string[] | null;
  /** The Actual shown in the cell, for the reconciliation line. */
  expected: number;
}

export default function GlDetailDrawer({
  projectId, jobNumber, target, onClose, through = null,
}: {
  projectId: number;
  jobNumber: string | null;
  target: GlDetailTarget;
  onClose: () => void;
  /** 'YYYY-MM-DD'. Set on a PAST month's view: only entries dated on or before it are
   *  listed, so the subtotal equals the figure that month's tab shows. The server cuts
   *  the same way (undated entries stay in). Null = every entry, the live tab. */
  through?: string | null;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, { onEscape: onClose });

  // One fetch per job, shared by every cell the user opens.
  const q = useQuery({
    queryKey: ['financials', projectId, 'gl-detail'],
    queryFn: () => financialsApi.glDetail(projectId),
    staleTime: 60_000,
  });

  const view = useMemo(() => {
    if (!q.data) return null;
    // The same filter the Actual-by-cost-type view uses (lib/glByGroup), so the two never list different entries.
    const lines = glLinesFor(q.data.lines, target.codes, through);
    const total = lines.reduce((s, l) => s + l.amount, 0);
    const byType = tally(lines, (l) => l.costType ?? '(blank)');
    const byAccount = tally(lines, (l) => `${l.accountNo ?? '—'} ${l.accountTitle ?? ''}`.trim());
    return { lines, total, byType, byAccount };
  }, [q.data, target.codes, through]);

  // Off by more than a dollar means the tab's figures and this list came from
  // different reads of the GL — worth saying rather than letting someone chase
  // a rounding ghost. Same table and same filter, so it should never fire.
  const drift = view ? Math.abs(view.total - target.expected) : 0;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 200 }} />
      <div
        ref={ref}
        role="dialog"
        aria-label={`GL detail for ${target.label}`}
        style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 900, maxWidth: '100vw', background: '#fff', zIndex: 201, overflowY: 'auto', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--ew-n-100)', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>
              GL detail · {target.label}
              {target.sub ? <span style={{ fontWeight: 400, color: 'var(--ew-n-500)' }}> · {target.sub}</span> : null}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ew-n-500)', marginTop: 2 }}>
              {view ? <>{view.lines.length.toLocaleString()} {view.lines.length === 1 ? 'entry' : 'entries'} · <b style={{ color: 'var(--ew-dark)' }}>{money(view.total)}</b></> : 'Loading…'}
              {through ? ` · dated through ${fmtDate(through)}` : ''}
              {q.data?.syncedAt ? ` · GL synced ${fmtDate(q.data.syncedAt)}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            <button
              onClick={() => view && exportGlLines(view.lines, jobNumber, target.label)}
              disabled={!view || view.lines.length === 0}
              style={{ fontSize: 12, fontWeight: 600, background: 'var(--ew-green)', color: '#fff', border: 'none', borderRadius: 'var(--ew-r-md)', padding: '6px 14px', cursor: view?.lines.length ? 'pointer' : 'not-allowed', opacity: view?.lines.length ? 1 : 0.5 }}
            >
              ⤓ Export to Excel
            </button>
            <button onClick={onClose} aria-label="Close drawer" style={{ fontSize: 18, color: 'var(--ew-n-500)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}>✕</button>
          </div>
        </div>

        <div style={{ padding: '14px 18px 32px' }}>
          <StateView query={q} empty="No GL entries for this job yet.">
            {() => {
              if (!view) return null;
              if (view.lines.length === 0) {
                return <div style={{ fontSize: 13, color: 'var(--ew-n-500)', padding: '24px 0' }}>No GL entries posted against {target.label} yet.</div>;
              }
              return (
                <>
                  {drift > 1 && (
                    <Callout tone="warn">
                      These entries total {money(view.total)} but the tab shows {money(target.expected)}. The
                      GL feed has moved since the tab loaded — refresh the page for figures that agree.
                    </Callout>
                  )}

                  <Section title="By Intacct cost type">
                    <Chips rows={view.byType} total={view.total} />
                    <div style={{ fontSize: 11.5, color: 'var(--ew-n-500)', marginTop: 8, lineHeight: 1.5 }}>
                      This is the cost type on each GL entry. The tab groups a cost <i>code</i> by the type
                      the <i>estimate</i> budgeted it as, so these can differ from the column the figure
                      appears in — actuals settle against the code they belong to rather than against a
                      type the estimate never used.
                    </div>
                  </Section>

                  <Section title="By GL account">
                    <Chips rows={view.byAccount} total={view.total} />
                  </Section>

                  <Section title={`Entries (${view.lines.length.toLocaleString()})`}>
                    <div className="ew-table-scroll">
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                        <thead>
                          <tr>
                            {['Date', 'Account', 'Cost Code', 'Cost Type', 'Document', 'Batch', 'Amount'].map((h, i) => (
                              <th key={h} style={{ textAlign: i === 6 ? 'right' : 'left', padding: '8px 10px', font: 'var(--ew-fw-semibold) 10.5px/1 var(--ew-font)', letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ew-n-500)', borderBottom: '1px solid var(--ew-n-100)', whiteSpace: 'nowrap' }}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {view.lines.map((l) => (
                            <tr key={l.recordNo}>
                              <td style={td}>{l.entryDate ? fmtDate(l.entryDate) : '—'}</td>
                              <td style={td}>
                                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{l.accountNo ?? '—'}</span>
                                {l.accountTitle ? <span style={{ color: 'var(--ew-n-500)' }}> · {l.accountTitle}</span> : null}
                              </td>
                              <td style={td}>
                                {l.costCode ?? <span style={{ color: 'var(--ew-n-400)' }}>(none)</span>}
                                {l.costCodeName ? <span style={{ color: 'var(--ew-n-500)' }}> · {l.costCodeName}</span> : null}
                              </td>
                              <td style={td}>{l.costType ?? <span style={{ color: 'var(--ew-n-400)' }}>(blank)</span>}</td>
                              <td style={td}>{l.document ?? '—'}</td>
                              <td style={td}>{l.batchNo ?? '—'}</td>
                              <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: l.amount < 0 ? 'var(--ew-green-700)' : undefined }}>{money(l.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={6} style={{ ...td, fontWeight: 700, borderTop: '2px solid var(--ew-n-200)', borderBottom: 'none' }}>Total</td>
                            <td style={{ ...td, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', borderTop: '2px solid var(--ew-n-200)', borderBottom: 'none' }}>{money(view.total)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--ew-n-500)', marginTop: 10, lineHeight: 1.5 }}>
                      Document and Batch are the drill-back references into Intacct. The GLDETAIL feed
                      carries no vendor or memo, so who was paid is not available here.
                      Negative amounts are credits and net against the total.
                    </div>
                  </Section>
                </>
              );
            }}
          </StateView>
        </div>
      </div>
    </>
  );
}

const td: React.CSSProperties = { padding: '7px 10px', borderBottom: '1px solid #F2F2F1', color: 'var(--ew-dark)', verticalAlign: 'top' };

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ew-dark)', borderBottom: '1px solid var(--ew-n-100)', paddingBottom: 6, marginBottom: 10 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Callout({ tone, children }: { tone: 'warn'; children: React.ReactNode }) {
  return (
    <div style={{ background: '#FBEEE1', border: '1px solid #F0D9C0', color: '#8A4E12', borderRadius: 'var(--ew-r-md)', padding: '9px 12px', fontSize: 12.5, lineHeight: 1.45, marginBottom: 16 }}>
      {tone === 'warn' ? '⚠ ' : ''}{children}
    </div>
  );
}

function Chips({ rows, total }: { rows: { key: string; amount: number; n: number }[]; total: number }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {rows.map((r) => (
        <span key={r.key} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, background: '#fff', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-pill)', padding: '3px 10px', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
          <b style={{ fontWeight: 600 }}>{r.key}</b>
          <span>{money(r.amount)}</span>
          <small style={{ color: 'var(--ew-n-500)' }}>
            {total ? `${Math.round((r.amount / total) * 100)}%` : '—'} · {r.n.toLocaleString()}
          </small>
        </span>
      ))}
    </div>
  );
}

/** Group lines by a key, largest absolute dollar first. */
function tally(lines: GlCostLine[], key: (l: GlCostLine) => string) {
  const m = new Map<string, { key: string; amount: number; n: number }>();
  for (const l of lines) {
    const k = key(l);
    const e = m.get(k) ?? { key: k, amount: 0, n: 0 };
    e.amount += l.amount; e.n += 1;
    m.set(k, e);
  }
  return [...m.values()].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
}

/**
 * Export exactly the lines on screen — same rule as the projects export: what
 * you are looking at is what lands in the file.
 *
 * Two sheets. `GL Lines` is the detail; `Summary` pivots cost code × cost type,
 * which is the shape finance reconciles in and saves them building it by hand.
 */
export function exportGlLines(lines: GlCostLine[], jobNumber: string | null, scope: string) {
  const header = ['Date', 'Account', 'Account Title', 'Cost Code', 'Cost Code Name', 'Cost Type', 'Document', 'Batch', 'Amount', 'GL Record'];
  const body = lines.map((l) => [
    // Date-safe: the field is an ISO string over the wire, but the helper also
    // takes a bare Date rather than throwing and aborting the whole export.
    toDateInputValue(l.entryDate),
    l.accountNo ?? '', l.accountTitle ?? '',
    l.costCode ?? '', l.costCodeName ?? '',
    l.costType ?? '',
    l.document ?? '', l.batchNo ?? '',
    Math.round(l.amount * 100) / 100,
    l.recordNo,
  ]);
  const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
  ws['!cols'] = [{ wch: 11 }, { wch: 9 }, { wch: 30 }, { wch: 13 }, { wch: 30 }, { wch: 11 }, { wch: 18 }, { wch: 12 }, { wch: 14 }, { wch: 22 }];
  ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: lines.length, c: header.length - 1 } }) };

  const pivot = new Map<string, { code: string; name: string; type: string; amount: number; n: number }>();
  for (const l of lines) {
    const code = l.costCode ?? '(none)', type = l.costType ?? '(blank)';
    const k = `${code}\u0000${type}`;   // NUL separator: cannot occur in a cost code or cost type
    const e = pivot.get(k) ?? { code, name: l.costCodeName ?? '', type, amount: 0, n: 0 };
    e.amount += l.amount; e.n += 1;
    pivot.set(k, e);
  }
  const sRows = [...pivot.values()].sort((a, b) => a.code.localeCompare(b.code) || a.type.localeCompare(b.type));
  const total = lines.reduce((s, l) => s + l.amount, 0);
  const wsS = XLSX.utils.aoa_to_sheet([
    ['Cost Code', 'Cost Code Name', 'Intacct Cost Type', 'Amount', 'Entries'],
    ...sRows.map((r) => [r.code, r.name, r.type, Math.round(r.amount * 100) / 100, r.n]),
    [],
    ['TOTAL', '', '', Math.round(total * 100) / 100, lines.length],
  ]);
  wsS['!cols'] = [{ wch: 14 }, { wch: 34 }, { wch: 18 }, { wch: 15 }, { wch: 9 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsS, 'Summary');
  XLSX.utils.book_append_sheet(wb, ws, 'GL Lines');
  const slug = scope.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'all';
  // The EXPORTER'S calendar date. ⚠️ Not `toDateInputValue(new Date().toISOString())`,
  // which is the UTC date: an Eastern user exporting after 8pm is already on the next
  // UTC day and files a workbook stamped TOMORROW. `todayInputValue` builds from local
  // components for exactly this reason, and says so in its own docblock. (The entry
  // dates above are a different case — those are real ISO values off the wire.)
  const stamp = todayInputValue();
  XLSX.writeFile(wb, `gl-detail-${jobNumber ?? 'job'}-${slug}-${stamp}.xlsx`);
}
